import { HttpBackend, HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';
import { AppService } from './app.service';
import { Location } from '../model/common.model';


@Injectable({
  providedIn: 'root'
})
export class LocationService {
  private http: HttpClient;
  constructor(httpBackend: HttpBackend, private appService: AppService) {
    this.http = new HttpClient(httpBackend);
  }
  postcodes = 'https://api.postcodes.io';
  overpass = 'https://overpass-api.de/api';
  cache = new Map<string, any>();

  firstValueFrom<T>(source: Observable<{ status: number, result: T } | any>, key?: string): Promise<T> {
    if (key && this.cache.has(key))
      return Promise.resolve(this.cache.get(key));

    return new Promise<T>((resolve, reject) => {
      source.subscribe({
        next: (value) => {
          if (value.status == 200) {
            const res = value.result || [];
            if (key) this.cache.set(key, res);
            resolve(res);
          }
          else
            reject(value);
        },
        error: reject,
        complete: () => {
          reject(new Error('Observable completed prematurely'));
        },
      });
    });
  }

  async distance(from: string, to: string, unit: 'mi' | 'km' | 'm' | 'ft' | 'imperial' | 'metric' = 'mi') {
    var first = await this.getPostcode(from);
    var second = await this.getPostcode(to);
    return Promise.resolve(this.calculateDistance(first.Latitude, first.Longitude, second.Latitude, second.Longitude, unit));
  }

  private calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number, unit: string) {
    const R = 6371e3; // metres

    let phi1 = lat1 * Math.PI / 180; // φ, λ in radians
    let phi2 = lat2 * Math.PI / 180;
    let deltaPhi = (lat2 - lat1) * Math.PI / 180;
    let deltaLambda = (lon2 - lon1) * Math.PI / 180;

    let a = Math.sin(deltaPhi / 2) * Math.sin(deltaPhi / 2) +
      Math.cos(phi1) * Math.cos(phi2) *
      Math.sin(deltaLambda / 2) * Math.sin(deltaLambda / 2);
    let c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    var distanceInMeters = R * c; // in metres
    switch (unit) {
      case 'km':
        return Math.round(distanceInMeters / 1000);
      case 'm':
        return distanceInMeters;
      case 'ft':
        return Math.round(distanceInMeters * 3.28084);
      case 'mi':
        return Math.round(distanceInMeters * 0.000621371);
      default:
      case 'imperial':
        var miles = Math.round(distanceInMeters * 0.000621371);
        if (miles < 1) return Math.round(distanceInMeters * 3.28084) + ' ft';
        return miles + ' mi';
      case 'metric':
        var km = Math.round(distanceInMeters / 1000);
        if (km < 1) return distanceInMeters + ' m';
        return km + ' km';
    }
  }

  async getPostcode(postcode: string): Promise<Location> {
    var response = await this.firstValueFrom<any>(this.http.get(`${this.postcodes}/postcodes/${postcode}`), `postcode-${postcode}`);
    return Promise.resolve({
      Postcode: response.postcode,
      Latitude: response.latitude,
      Longitude: response.longitude,
      City: response.admin_district,
      County: response.admin_county,
      Country: response.country
    });
  }

  getNearestPostcodes(postcode: string) {
    return this.firstValueFrom(this.http.get(`${this.postcodes}/postcodes/${postcode}/nearest`));
  }

  getPostcodeDetails(postcode: string) {
    return this.firstValueFrom(this.http.get(`${this.postcodes}/postcodes/${postcode}/validate`));
  }

  getPostcodeAutocomplete(postcode: string) {
    return this.firstValueFrom<string[]>(this.http.get(`${this.postcodes}/postcodes/${postcode}/autocomplete`), `autocomplete-${postcode}`);
  }

  getPostcodeBulk(postcodes: string[]) {
    return this.firstValueFrom(this.http.post(`${this.postcodes}/postcodes`, { postcodes }));
  }

  getPostcodeQuery(postcode: string) {
    return this.firstValueFrom(this.http.get(`${this.postcodes}/postcodes?q=${postcode}`));
  }

  getPostcodeReverse(latitude: number, longitude: number) {
    return this.firstValueFrom(this.http.get(`${this.postcodes}/postcodes?lon=${longitude}&lat=${latitude}`));
  }

  getPostcodeReverseBulk(postcodes: string[]) {
    return this.firstValueFrom(this.http.post(`${this.postcodes}/postcodes`, { geolocations: postcodes }));
  }

  getOutcode(outcode: string) {
    return this.firstValueFrom(this.http.get(`${this.postcodes}/outcodes/${outcode}`));
  }

  getOutcodeDetails(outcode: string) {
    return this.firstValueFrom(this.http.get(`${this.postcodes}/outcodes/${outcode}/validate`));
  }

  getOutcodeBulk(outcodes: string[]) {
    return this.firstValueFrom(this.http.post(`${this.postcodes}/outcodes`, { outcodes }));
  }

  getOutcodeQuery(outcode: string): Promise<Array<{ outcode: string } | string>> {
    return this.firstValueFrom(this.http.get(`${this.postcodes}/outcodes?q=${encodeURIComponent(outcode)}`));
  }

  getOutcodeReverse(latitude: number, longitude: number) {
    return this.firstValueFrom(this.http.get(`${this.postcodes}/outcodes?lon=${longitude}&lat=${latitude}`));
  }

  getOutcodeReverseBulk(outcodes: string[]) {
    return this.firstValueFrom(this.http.post(`${this.postcodes}/outcodes`, { geolocations: outcodes }));
  }

  async searchPostcode(query: string, outcodes = false) {
    const normalizedQuery = query?.trim().toUpperCase();
    if (!normalizedQuery) return [];

    if (!outcodes)
      return await this.getPostcodeAutocomplete(normalizedQuery) || [];

    // Postcode autocomplete is intended for complete postcodes and can return no
    // results (or reject) for short, valid outcodes such as N2. Search the outcode
    // endpoint as well so delivery zones can be configured with an outcode alone.
    const [postcodes, outcodeMatches] = await Promise.all([
      this.getPostcodeAutocomplete(normalizedQuery).catch(() => []),
      this.getOutcodeQuery(normalizedQuery).catch(() => [])
    ]);
    // Keep an already complete outcode selectable even when postcodes.io does
    // not return it from either search endpoint. In particular, autocomplete
    // can treat short outcodes such as N2 and EN1 as incomplete postcodes.
    const enteredOutcode = /^[A-Z]{1,2}\d[A-Z\d]?$/.test(normalizedQuery)
      ? [normalizedQuery]
      : [];
    const matchedOutcodes = outcodeMatches
      .map(_ => typeof _ === 'string' ? _ : _.outcode)
      .filter(_ => !!_);
    const postcodeOutcodes = postcodes.map(_ => _.split(' ')[0]);

    return [...new Set([...enteredOutcode, ...matchedOutcodes, ...postcodeOutcodes, ...postcodes])];
  }

  async getRoadName(postcode: string) {
    const loc = await this.getPostcode(postcode);
    let radius = 50;
    do {
      const [first, ..._] = await this.findRoads(loc.Latitude, loc.Longitude, radius, 1);
      if (first) return first.name;
      radius += 100;
    } while (radius <= 1000);
  }

  async detectUkPostcodeFromCoordinates(latitude: number, longitude: number): Promise<string> {
    const response = await firstValueFrom<any>(this.http.get(`/api/location/reverse-postcode?lat=${latitude}&lon=${longitude}`));
    return response?.postcode;
  }

  async findAddresses(postcode: string, businessId?: string): Promise<Location[]> {
    const normalized = (postcode || '').replace(/\s+/g, '').toUpperCase();
    const id = businessId || this.appService.activeBusiness?.Id;
    const query = id ? `?businessId=${encodeURIComponent(id)}` : '';
    return await firstValueFrom(this.http.get<Location[]>(`/api/location/addresses/${encodeURIComponent(normalized)}${query}`));
  }

  async findRoads(lat: number, lon: number, radius: number, limit: number) {
    const { elements } = await firstValueFrom<any>(this.http.post(`${this.overpass}/interpreter`, `[out:json][timeout:15];
way(around:${radius},${lat},${lon})["highway"]["name"];
out tags ${limit};`));
    return elements.map(_ => _.tags);
  }
}
